import { createFileRoute } from '@tanstack/react-router'
import { message } from 'antd'
// TODO: Put the required fetchers in the Tanstack context so these can run in a beforeLoad
// eslint-disable-next-line boundaries/dependencies
import { hasAvailableUtIndicationsDataset } from 'features/derivedSpatialDatasets'
// eslint-disable-next-line boundaries/dependencies
import { useRolodexEntries } from 'features/rolodex'
import { useShouldRouteToForgingsPage } from 'pages/viewer/ViewerRouteNavigator'
import React from 'react'
import { viewerRoutingSearchParams } from 'routes/viewer-routing/-searchParams'
import {
  getSortedAssetModels,
  KestrelInspectionType,
  RolodexEntryType,
} from 'types'

/**
 * A dispatcher route that decides which "special-case viewer" page to send the
 * user to, based on the identifiers it is given and the data associated with
 * them. Centralize all branching logic here.
 */
function RouteComponent() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { unitId, inspectionId, componentId, cdf, preferredEndpoint } = search

  const rolodexQuery = React.useMemo(() => {
    if (unitId) {
      return {
        type: [
          RolodexEntryType.ASSET_MODEL,
          RolodexEntryType.INSPECTION,
          RolodexEntryType.COMPONENT,
          RolodexEntryType.DERIVED_SPATIAL_DATASET,
        ],
        tags: { [RolodexEntryType.UNIT]: unitId },
        limit: 10000,
      }
    }
    if (inspectionId) {
      return {
        type: [
          RolodexEntryType.INSPECTION,
          RolodexEntryType.DERIVED_SPATIAL_DATASET,
        ],
        tags: { inspection: inspectionId },
        limit: 10000,
      }
    }
    return undefined
  }, [unitId, inspectionId])

  const {
    assetModels,
    inspections,
    derivedSpatialDatasets,
    isLoading,
    hasEverLoaded,
  } = useRolodexEntries(rolodexQuery)

  const {
    kestrelModels,
    isLoading: isKestrelLoading,
    hasEverLoaded: hasKestrelEverLoaded,
  } = useRolodexEntries(
    unitId
      ? {
          type: RolodexEntryType.KESTREL_MODEL,
          tags: { [RolodexEntryType.UNIT]: unitId },
          filters: [
            {
              key: 'inspection_type',
              op: '=',
              value: KestrelInspectionType.SITEVIEW,
            },
          ],
        }
      : undefined
  )
  const hasSiteviewKestrelModel = kestrelModels.length > 0

  const forgingRouting = useShouldRouteToForgingsPage(unitId ?? '')

  // Wait for the data our decision depends on. `useShouldRouteToForgingsPage`
  // is only meaningful (and only actually fetches) when we have a unit.
  if (rolodexQuery && (isLoading || !hasEverLoaded)) {
    return null
  }
  if (unitId && (isKestrelLoading || !hasKestrelEverLoaded)) {
    return null
  }
  if (unitId && forgingRouting.isLoading) {
    return null
  }

  // 1. Inspection with UT indications -> forgings indications viewer.
  if (
    inspectionId &&
    hasAvailableUtIndicationsDataset(derivedSpatialDatasets, inspectionId)
  ) {
    navigate({
      to: '/forgings-indications/$inspectionId',
      params: { inspectionId },
      replace: true,
    })
    return null
  }

  // 2. Unit with a SiteView Kestrel model -> system view.
  if (unitId && hasSiteviewKestrelModel) {
    navigate({
      to: '/systemview/$entryID',
      params: { entryID: unitId },
      replace: true,
    })
    return null
  }

  // 3. Forging unit with legacy point cloud data -> details viewer for the unit.
  if (unitId && !forgingRouting.isLoading && forgingRouting.shouldRoute) {
    navigate({
      to: '/details/$entryID',
      params: { entryID: unitId },
      replace: true,
    })
    return null
  }

  // 4. Explicit request for the multi-component viewer (MCV), when configured.
  // Gated behind `preferredEndpoint` so that the plain "open this unit" case
  // falls through to component details rather than silently sending users into
  // the (permission-gated, beta) full-asset viewer.
  const assetModel = getSortedAssetModels([...assetModels]).at(0)
  const isMCVConfigured = Array.from(
    assetModel?.data.components?.values() ?? []
  ).some((component) => component.sceneConfig)

  if (unitId && preferredEndpoint && isMCVConfigured) {
    if (preferredEndpoint === 'configuration') {
      navigate({
        to: '/viewer/$unitID/configuration',
        params: { unitID: unitId },
        replace: true,
      })
    } else {
      navigate({
        to: '/viewer/$unitID/inspection',
        params: { unitID: unitId },
        replace: true,
      })
    }
    return null
  }

  // 5. A specific component / inspection to open -> details viewer.
  const componentToView =
    componentId ??
    inspections
      .toSorted(
        (a, b) => (b.data.date?.getTime() ?? 0) - (a.data.date?.getTime() ?? 0)
      )
      .at(0)?.tags.component

  if (componentToView) {
    navigate({
      to: '/details/$entryID',
      params: { entryID: componentToView },
      search: { cdf, inspectionId },
      replace: true,
    })
    return null
  }

  message.error('Unable to open viewer: not enough information was provided.')
  return null
}

export const Route = createFileRoute('/viewer-routing/')({
  validateSearch: viewerRoutingSearchParams,
  component: RouteComponent,
})
